import React from 'react';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const InvoiceSummaryReport = ({ data }) => {
  if (!data || !data.data) {
    return <div>No data available</div>;
  }

  const summary = data.data;

  // Debug logging
  console.log('📊 InvoiceSummaryReport received data:', summary);
  console.log('💰 Sales value type:', typeof summary.total_sales_value, 'Value:', summary.total_sales_value);
  console.log('💰 Tax charged type:', typeof summary.total_tax_charged, 'Value:', summary.total_tax_charged);

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  };

  const formatNumber = (num) => {
    const numValue = parseInt(num) || 0;
    return new Intl.NumberFormat('en-PK').format(numValue);
  };

  const stats = [
    {
      name: 'Total Invoices',
      value: formatNumber(summary.total_invoices),
      icon: DocumentTextIcon,
      color: 'blue'
    },
    {
      name: 'Total Sales Value (Excluding Tax)',
      value: formatCurrency(summary.total_excluding_tax || summary.total_sales_value),
      icon: CurrencyDollarIcon,
      color: 'green'
    },
    {
      name: 'Total Tax Charged',
      value: formatCurrency(summary.total_tax_charged),
      icon: CurrencyDollarIcon,
      color: 'purple'
    },
    {
      name: 'Total Amount (Including Tax)',
      value: formatCurrency(summary.total_including_tax || summary.total_amount_payable),
      icon: CurrencyDollarIcon,
      color: 'indigo'
    },
    {
      name: 'Average Invoice Value',
      value: formatCurrency(summary.avg_invoice_value),
      icon: CurrencyDollarIcon,
      color: 'orange'
    }
  ];

  const statusStats = [
    {
      name: 'Submitted',
      value: formatNumber(summary.submitted_count),
      icon: CheckCircleIcon,
      color: 'green'
    },
    {
      name: 'Validated',
      value: formatNumber(summary.validated_count),
      icon: CheckCircleIcon,
      color: 'blue'
    },
    {
      name: 'Pending',
      value: formatNumber(summary.pending_count),
      icon: ClockIcon,
      color: 'yellow'
    },
    {
      name: 'Error',
      value: formatNumber(summary.error_count),
      icon: XCircleIcon,
      color: 'red'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Key Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center">
                  <div className={`p-2 rounded-md bg-${stat.color}-100`}>
                    <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Breakdown */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Status Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statusStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center">
                  <div className={`p-2 rounded-md bg-${stat.color}-100`}>
                    <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Date Range Info */}
      {(summary.earliest_date || summary.latest_date) && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Date Range</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            {summary.earliest_date && (
              <div>
                <span className="font-medium">Earliest Invoice:</span>{' '}
                {new Date(summary.earliest_date).toLocaleDateString()}
              </div>
            )}
            {summary.latest_date && (
              <div>
                <span className="font-medium">Latest Invoice:</span>{' '}
                {new Date(summary.latest_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success Rate */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Success Rate</h4>
        <div className="space-y-4">
          {summary.total_invoices > 0 && (
            <>
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Submitted Successfully</span>
                  <span>{Math.round((summary.submitted_count / summary.total_invoices) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ width: `${(summary.submitted_count / summary.total_invoices) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Validation Rate</span>
                  <span>{Math.round(((summary.submitted_count + summary.validated_count) / summary.total_invoices) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${((summary.submitted_count + summary.validated_count) / summary.total_invoices) * 100}%` }}
                  ></div>
                </div>
              </div>

              {summary.error_count > 0 && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Error Rate</span>
                    <span>{Math.round((summary.error_count / summary.total_invoices) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full" 
                      style={{ width: `${(summary.error_count / summary.total_invoices) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tax Calculation Breakdown */}
      {(summary.calculated_sales_from_items || summary.calculated_tax_from_items) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Calculation Breakdown (Debug)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-yellow-700">
            <div><span className="font-medium">Sales from Items:</span> {formatCurrency(summary.calculated_sales_from_items)}</div>
            <div><span className="font-medium">Tax from Items:</span> {formatCurrency(summary.calculated_tax_from_items)}</div>
            <div><span className="font-medium">Excluding Tax Total:</span> {formatCurrency(summary.total_excluding_tax)}</div>
            <div><span className="font-medium">Including Tax Total:</span> {formatCurrency(summary.total_including_tax)}</div>
          </div>
        </div>
      )}

      {/* Tax Rate Analysis */}
      {summary.total_sales_value > 0 && summary.total_tax_charged > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Tax Analysis</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <span className="font-medium">Effective Tax Rate:</span>{' '}
              {((summary.total_tax_charged / (summary.total_excluding_tax || summary.total_sales_value)) * 100).toFixed(2)}%
            </div>
            <div>
              <span className="font-medium">Tax to Sales Ratio:</span>{' '}
              {(summary.total_tax_charged / summary.total_sales_value).toFixed(2)}
            </div>
            <div>
              <span className="font-medium">Total Payable:</span>{' '}
              {formatCurrency((summary.total_excluding_tax || summary.total_sales_value) + summary.total_tax_charged)}
            </div>
          </div>
        </div>
      )}

      {/* Applied Filters */}
      {data.filters && Object.values(data.filters).some(v => v) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Applied Filters</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm text-blue-700">
            {data.filters.startDate && (
              <div><span className="font-medium">Start Date:</span> {data.filters.startDate}</div>
            )}
            {data.filters.endDate && (
              <div><span className="font-medium">End Date:</span> {data.filters.endDate}</div>
            )}
            {data.filters.status && (
              <div><span className="font-medium">Status:</span> {data.filters.status}</div>
            )}
            {data.filters.province && (
              <div><span className="font-medium">Province:</span> {data.filters.province}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceSummaryReport;
